import { supabase } from "../lib/supabase";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";

/**
 * Obtiene el access_token actual de Supabase Auth.
 * Se llama en cada request para garantizar que el token esté fresco
 * (supabase-js maneja el refresh automáticamente).
 */
async function getToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

async function request(path, options = {}) {
  const token = await getToken();
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  // Inyectar Authorization header si hay sesión activa
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  // Si el backend responde 401, la sesión expiró o es inválida
  if (res.status === 401) {
    // Intentar refresh del token
    const { error } = await supabase.auth.refreshSession();
    if (error) {
      // Si falla el refresh, desloguear al usuario
      await supabase.auth.signOut();
      window.location.replace("/");
      throw new Error("Sesión expirada. Inicia sesión de nuevo.");
    }
    // Reintentar la request con el token nuevo
    const newToken = await getToken();
    if (newToken) {
      headers["Authorization"] = `Bearer ${newToken}`;
    }
    const retryRes = await fetch(`${API_BASE}${path}`, { ...options, headers });
    if (!retryRes.ok) throw new Error(`API ${retryRes.status}: ${await retryRes.text()}`);
    const retryJson = await retryRes.json();
    if (!retryJson.ok) throw new Error(retryJson.detail || "API error");
    return retryJson.data;
  }

  if (!res.ok) {
    const text = await res.text();
    // Detectar 403 por límite de plan y disparar evento global
    if (res.status === 403) {
      let detail = text;
      try { detail = JSON.parse(text)?.detail ?? text; } catch {}
      if (typeof detail === "string" && detail.includes("límite de tu plan")) {
        window.dispatchEvent(new CustomEvent("plan-limit-reached", { detail }));
      }
    }
    throw new Error(`API ${res.status}: ${text}`);
  }
  const json = await res.json();
  if (!json.ok) throw new Error(json.detail || "API error");
  return json.data;
}

export const api = {
  meses: {
    get: (anio, mes) => request(`/meses/${anio}/${mes}`),
    getYear: (anio) => request(`/meses/year/${anio}`),
  },

  ingresos: {
    list: (mesId) => request(`/ingresos/${mesId}`),
    create: (data) =>
      request("/ingresos/", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) =>
      request(`/ingresos/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id) => request(`/ingresos/${id}`, { method: "DELETE" }),
  },

  gastos: {
    extras: {
      list: (mesId) => request(`/gastos/extras/${mesId}`),
      create: (data) =>
        request("/gastos/extras", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id, data) =>
        request(`/gastos/extras/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      remove: (id) =>
        request(`/gastos/extras/${id}`, { method: "DELETE" }),
    },

    fijos: {
      list: (mesId) => request(`/gastos/fijos/${mesId}`),
      create: (data) =>
        request("/gastos/fijos", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      update: (id, data) =>
        request(`/gastos/fijos/${id}`, {
          method: "PATCH",
          body: JSON.stringify(data),
        }),
      remove: (id) =>
        request(`/gastos/fijos/${id}`, { method: "DELETE" }),
    },
  },

  recurrentes: {
    list: () => request("/recurrentes/"),
    create: (data) =>
      request("/recurrentes/", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) =>
      request(`/recurrentes/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id) => request(`/recurrentes/${id}`, { method: "DELETE" }),
  },

  metas: {
    list: () => request("/metas/"),
    create: (data) =>
      request("/metas/", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) =>
      request(`/metas/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id) => request(`/metas/${id}`, { method: "DELETE" }),

    ahorros: {
      list: (mesId) => request(`/metas/ahorros/${mesId}`),
      upsert: (data) =>
        request("/metas/ahorros", {
          method: "POST",
          body: JSON.stringify(data),
        }),
    },
  },

  presupuestos: {
    list: () => request("/presupuestos/"),
    create: (data) =>
      request("/presupuestos/", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id, data) =>
      request(`/presupuestos/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    remove: (id) => request(`/presupuestos/${id}`, { method: "DELETE" }),
  },

  msi: {
    compras: {
      list: () => request("/msi/compras"),
      create: (data) =>
        request("/msi/compras", {
          method: "POST",
          body: JSON.stringify(data),
        }),
      remove: (id) => request(`/msi/compras/${id}`, { method: "DELETE" }),
    },

    pagos: {
      list: (compraId) => request(`/msi/pagos/${compraId}`),
      toggle: (pagoId) =>
        request(`/msi/pagos/${pagoId}`, { method: "PATCH" }),
    },
  },

  duky: {
    chat: (messages, { mes_id, mes_label } = {}) =>
      request("/duky/chat", {
        method: "POST",
        body: JSON.stringify({ messages, mes_id, mes_label }),
      }),
  },

  cuentas: {
    list: () => request("/cuentas/"),
    create: (data) =>
      request("/cuentas/", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) =>
      request(`/cuentas/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (id) => request(`/cuentas/${id}`, { method: "DELETE" }),
    // adjustSaldo removido: el endpoint ignoraba su propio `delta`.
    // El saldo lo recalcula el backend desde los movimientos.
  },

  categorias: {
    list: () => request("/categorias/"),
    create: (data) =>
      request("/categorias/", { method: "POST", body: JSON.stringify(data) }),
    update: (id, data) =>
      request(`/categorias/${id}`, { method: "PATCH", body: JSON.stringify(data) }),
    remove: (id) => request(`/categorias/${id}`, { method: "DELETE" }),
  },

  transferencias: {
    list: () => request("/transferencias/"),
    create: (data) =>
      request("/transferencias/", { method: "POST", body: JSON.stringify(data) }),
    remove: (id) => request(`/transferencias/${id}`, { method: "DELETE" }),
  },

  preferencias: {
    getAll: () => request("/preferencias/"),
    upsert: (clave, valor) =>
      request("/preferencias/", {
        method: "POST",
        body: JSON.stringify({ clave, valor }),
      }),
  },

  notifications: {
    // Registrar una nueva suscripción push en el backend
    subscribe: (subscription) =>
      request("/notifications/subscribe", {
        method: "POST",
        body: JSON.stringify(subscription),
      }),

    // Eliminar una suscripción push por su endpoint
    unsubscribe: (endpoint) =>
      request("/notifications/unsubscribe", {
        method: "DELETE",
        body: JSON.stringify({ endpoint }),
      }),

    // Obtener preferencias de notificación del usuario
    getPreferences: () => request("/notifications/preferences"),

    // Actualizar preferencias de notificación del usuario
    updatePreferences: (prefs) =>
      request("/notifications/preferences", {
        method: "PUT",
        body: JSON.stringify(prefs),
      }),

    // Enviar una notificación de prueba (solo desarrollo)
    test: () =>
      request("/notifications/test", { method: "POST" }),
  },

  subscriptions: {
    get: () => request("/subscriptions/me"),
    cancel: () => request("/subscriptions/cancel", { method: "POST" }),
    reactivate: () => request("/subscriptions/reactivate", { method: "POST" }),
    // Activa trial de 15 días sin tarjeta (1 sola vez por usuario)
    startTrial: (plan) =>
      request("/subscriptions/start-trial", {
        method: "POST",
        body: JSON.stringify({ plan }),
      }),
    // Inicia Stripe Checkout y retorna { checkout_url }
    checkout: (plan) =>
      request("/subscriptions/checkout", {
        method: "POST",
        body: JSON.stringify({ plan }),
      }),
    // Retorna { portal_url } para Stripe Billing Portal
    billingPortal: () =>
      request("/subscriptions/billing-portal", { method: "POST" }),
    // Límites y uso del plan actual
    getLimits: () => request("/subscriptions/limits"),
  },

  admin: {
    listUsers: ({ limit = 50, offset = 0 } = {}) =>
      request(`/admin/users?limit=${limit}&offset=${offset}`),
    assignPlan: (userId, plan, periodEnd) =>
      request(`/admin/users/${userId}/plan`, {
        method: "POST",
        body: JSON.stringify({ plan, period_end: periodEnd }),
      }),
  },

  account: {
    requestDeletion: (reason) =>
      request("/account/request-deletion", {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
      }),
    cancelDeletion: () =>
      request("/account/cancel-deletion", { method: "POST" }),
  },

  ingestas: {
    /**
     * Envía audio al backend para procesamiento con Gemini.
     * Requiere FormData con el archivo de audio.
     * No usa request() porque FormData no debe tener Content-Type manual.
     */
    audio: async (audioBlob) => {
      const token = await getToken();
      const formData = new FormData();
      formData.append("audio", audioBlob, "recording.webm");

      const res = await fetch(`${API_BASE}/ingestas/audio`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });

      if (!res.ok) throw new Error(`API ${res.status}: ${await res.text()}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.detail || "Error procesando audio");
      return json.data;
    },
  },
};
